import { capitalizeString, sortDateDesc } from '@strata/helper-functions';
import { TreatmentStatusIndicator } from '@strata/react-status-indicators';

import { formatRelationshipCodeDisplay } from 'services/enrollmentHistoryHelpers';

/**
 * Returns the minimum date between the treatment start and the last referral start.
 * @param {Object} param - The object containing the treatment start and last referral start.
 * @param {string} param.treatmentStart - The treatment start date.
 * @param {string} param.lastReferralStart - The last referral start date.
 * @returns {string|undefined} The minimum date between the treatment start and the last referral start, or undefined if treatment start is null.
 */
export const getUnmanageOrDiscontinueMinDate = ({ treatmentStart, lastReferralStart }) => {
    // No treatment start, means not auth-based. This is a valid, but future scenario.
    if (treatmentStart == null) {
        return undefined;
    }
    // No last referral start means should be Unmanaged, or Discontinued
    if (lastReferralStart == null) {
        return treatmentStart;
    }
    return lastReferralStart;
};

/**
 * Object containing the summary of a treatment referral.
 * @typedef {Object} TreatmentReferralSummary
 * @property {string} referralId - The referral ID.
 * @property {string} friendlyId - The friendly ID.
 * @property {string} authId - The auth ID.
 * @property {string} servicingSupplierNpi - The servicing supplier NPI.
 * @property {string} routeName - The route name.
 * @property {string} benefitType - The benefit type.
 * @property {string} benefitTypeRaw - The unformatted benefit type.
 * @property {string} receivedTime - The received time.
 * @property {string} referralStatus - The referral status.
 */

/**
 * Mapped `active_referral` from treatment details — not interchangeable with {@link TreatmentReferralSummary}.
 * List rows come from `details.referrals` with a different mapper (snake_case referral rows; no `grandfathered`, no `servicing_info` rate snapshot, different `authId` source).
 * This snapshot uses sorted `auths` for `authId`, `servicing_info` for supplier/rate fields, and includes `grandfathered`.
 * @typedef {Object} TreatmentActiveReferralSnapshot
 * @property {string} referralId
 * @property {string|undefined} authId
 * @property {string} benefitType
 * @property {string} benefitTypeRaw
 * @property {string|undefined} servicingSupplierNpi
 * @property {string|undefined} priceSource
 * @property {boolean|undefined} priceIsFrozen
 * @property {string|null|undefined} receivedTime
 * @property {boolean} grandfathered - From API `active_referral.grandfathered` (required when `active_referral` is present); coerced to boolean.
 */

/**
 * Maps treatment referral summary from API.
 * @param {*} referral - The referral object from the API.
 * @returns {TreatmentReferralSummary} The treatment referral summary object.
 */
const mapTreatmentReferralSummary = (referral) => ({
    referralId: referral.referral_id,
    friendlyId: referral.friendly_id,
    authId: referral.auth_id,
    servicingSupplierNpi: referral.servicing_parent_pharmacy_npi,
    routeName: referral.route_name,
    benefitType: capitalizeString(referral.benefit_type),
    benefitTypeRaw: referral.benefit_type,
    receivedTime: referral.created_at,
    referralStatus: referral.referral_status,
});

const AuthSourceToDisplayName = {
    AGORA_AUTH: 'Agora Auth',
    DROPPER_AUTH: 'Dropper Auth',
    EXTERNAL: 'External',
    HISTORICAL: 'Historical',
    PBM_API: 'PBM API',
    OTHER: 'Other',
};

/**
 * Object containing the summary of a treatment authorization.
 * @typedef {Object} TreatmentAuthSummary
 * @property {string} authSource - The source of the authorization.
 * @property {string} caseId - The case ID.
 * @property {string} authStart - The authorization start date.
 * @property {string} authExpiration - The authorization expiration date.
 * @property {string} receivedAt - The received time (UTC datetime).
 * @property {string} billingProvider - The billing provider.
 * @property {string} servicingProvider - The servicing provider.
 * @property {string} requestingProvider - The requesting provider.
 * @property {string} servicingFacility - The servicing facility.
 */

/**
 * Maps treatment auth summary from API.
 * @param {*} auth - The auth object from the API.
 * @returns {TreatmentAuthSummary} The treatment auth summary object.
 */
const mapTreatmentAuthSummary = (auth) => ({
    authSource: AuthSourceToDisplayName[auth.auth_source] ?? auth.auth_source,
    caseId: auth.auth_id,
    authStart: auth.auth_start,
    authExpiration: auth.auth_expiration,
    receivedAt: auth.received_time,
    billingProvider: auth.billing_provider,
    servicingProvider: auth.servicing_provider,
    requestingProvider: auth.requesting_provider,
    servicingFacility: auth.servicing_facility,
});

/**
 * Object containing the summary of a treatment member.
 * @typedef {Object} TreatmentMemberSummary
 * @property {string} memberId - The member ID.
 * @property {string} firstName - The first name of the member.
 * @property {string} lastName - The last name of the member.
 * @property {string} gender - The gender of the member.
 * @property {string} dateOfBirth - The date of birth of the member.
 * @property {string} benefitGroup - The benefit group of the member.
 * @property {string} benefitGroupName - The benefit group name of the member.
 * @property {string} carrier - The carrier of the member.
 * @property {string} account - The account of the member.
 * @property {string} memberAddress1 - The first line of the address of the member.
 * @property {string} memberAddress2 - The second line of the address of the member.
 * @property {string} memberCity - The city of the member.
 * @property {string} memberState - The state of the member.
 * @property {string} memberZip - The zip code of the member.
 * @property {string} phone - The phone number of the member.
 * @property {string} email - The email address of the member.
 * @property {string} hasSecondaryCoverage - Whether the member has secondary coverage.
 * @property {string} lineOfBusiness - The line of business of the member.
 * @property {string} lobSegment - The LOB segment of the member.
 * @property {string} enrollmentStatus - The enrollment status of the member.
 * @property {string} [pendingAt] - When the member entered PENDING (`pending_at`), ISO string when API provides it.
 * @property {string} [willBeUnmanagedOn] - YYYY-MM-DD; set in Agora from `pending_at` + segment delayed-unmanagement days.
 * @property {string} [lastUpdated] - `scope.member.last_updated` from scope-assessor.
 * @property {string} [enrollmentChangeDate] - When enrollment data last changed (`enrollment_change_date`).
 * @property {string} [enrollmentCheckDate] - When an enrollment check last ran (`enrollment_check_date`).
 */

const formatEnrollmentSource = (source) => {
    if (!source) {
        return undefined;
    }
    if (source === 'MIRTH') {
        return 'API';
    }
    if (source === 'LOCAL') {
        return 'Enrollment File';
    }
    return undefined;
};

/**
 * Maps treatment member summary from API.
 * @param {*} member - The member object from the API.
 * @returns {MemberSummary} The treatment member summary object.
 */
export const mapMemberSummary = (member) => ({
    memberId: member.member_id,
    firstName: member.firstname,
    lastName: member.lastname,
    gender: member.gender,
    dateOfBirth: member.dateofbirth,
    benefitGroup: member.benefit_group,
    benefitGroupName: member.benefit_group_name,
    carrier: member.carrier,
    account: member.account,
    memberAddress1: member.member_address1,
    memberAddress2: member.member_address2,
    memberCity: member.member_city,
    memberState: member.member_state,
    memberZip: member.member_zip,
    phone: member.phone,
    secondaryPhone: member.secondary_phone,
    email: member.email,
    hasSecondaryCoverage: member.secondary_coverage_flag ? 'Yes' : 'No',
    lineOfBusiness: member.line_of_business,
    lobSegment: member.lob_segment,
    enrollmentStart: member.enrollment_start,
    enrollmentEnd: member.enrollment_end,
    enrollmentStatus: member.enrollment_status,
    enrollmentSource: formatEnrollmentSource(member.enrollment_source),
    enrollmentChangeDate: member.enrollment_change_date,
    enrollmentCheckDate: member.enrollment_check_date,
    isFmhEnrollmentEnd: member.is_fmh_enrollment_end,
    willBeUnmanagedOn: member.will_be_unmanaged_on,
    pendingAt: member.pending_at,
    lastUpdated: member.last_updated,
    benefitCode: member.benefit_code,
    insuranceType: member.insurance_type,
    relationshipCode: member.relationship_code,
    relationshipCodeDetailed: formatRelationshipCodeDisplay(member.relationship_code),
    memberPin: member.member_pin,
    isAso: member.is_aso ? 'True' : 'False',
    pharmacyCoverage: member.pharmacy_coverage,
    isMarketplaceEligible: member.is_marketplace_eligible ? 'Yes' : 'No',
    isApiBenefitCheckEnabled: member.is_api_benefit_check_enabled,
});

/**
 * Object containing the summary of a treatment.
 * @typedef {Object} TreatmentSummary
 * @property {string} payerId - The payer ID.
 * @property {string} treatmentId - The treatment ID.
 * @property {string} segmentId - The segment ID.
 * @property {boolean} isBrand - Whether the product is brand or generic.
 * @property {string} benefitType - The benefit type.
 * @property {string} placeOfService - The place of service.
 * @property {string} productServiceOid - The product service OID.
 * @property {string} productServiceId - The current product service ID (NDC).
 * @property {string} treatmentStatus - The treatment status.
 * @property {boolean} isActive - Whether the treatment is active.
 * @property {string} treatmentStart - The treatment start date.
 * @property {string} lastReferralStart - The last referral start date.
 * @property {string} firstName - The first name of the patient.
 * @property {string} lastName - The last name of the patient.
 * @property {TreatmentActiveReferralSnapshot|undefined} activeReferral - Undefined when the treatment has no `active_referral`; otherwise a snapshot that includes `grandfathered` (required on that object per API contract).
 * @property {Array<TreatmentReferralSummary>} referralList - The list of referral summaries.
 * @property {Array<TreatmentAuthSummary>} authList - The list of auth summaries.
 * @property {TreatmentMemberSummary} memberDetails - The member details summary.
 */

/**
 * Maps treatment details from API.
 *
 * @returns {TreatmentSummary} The simplified treatment details object.
 */
export const mapTreatmentDetails = (details) => {
    const referralList = details.referrals.map(mapTreatmentReferralSummary);
    const activeReferral = details.active_referral
        ? {
              referralId: details.active_referral.referral_id,
              authId: details.active_referral.auths?.sort((a, b) =>
                  sortDateDesc(a.auth_expiration, b.auth_expiration),
              )[0]?.auth_id,
              benefitType: capitalizeString(details.active_referral.benefit_type),
              benefitTypeRaw: details.active_referral.benefit_type,
              servicingSupplierNpi:
                  details.active_referral?.servicing_info?.servicing_parent_pharmacy_npi,
              priceSource: details.active_referral?.servicing_info?.servicing_rate?.price_source,
              priceIsFrozen:
                  details.active_referral?.servicing_info?.servicing_rate?.price_is_frozen,
              receivedTime:
                  details.referrals?.find(
                      (r) => r.referral_id === details.active_referral?.referral_id,
                  )?.created_at ?? null,
              grandfathered: details.active_referral.grandfathered,
              routeCode: details.active_referral.route_code,
          }
        : undefined;

    return {
        payerId: details.payer_id,
        treatmentId: details.treatment_id,
        treatmentCohortId: details.treatment_cohort_id ?? null,
        segmentId: details.segment_id,
        isBrand: !!details.is_brand,
        benefitType: details.benefit_type,
        placeOfService: details.place_of_service,
        productServiceOid: details.product_service_oid,
        productServiceId: details.product_service_id,
        treatmentStatus: details.treatment_status,
        isActive: details.treatment_status === TreatmentStatusIndicator.Statuses.ACTIVE,
        isBlocklisted: details.is_blocklisted,
        blocklistedReason: details.blocklisted_reason,
        treatmentStart: details.treatment_start,
        lastReferralStart: details.last_referral_start,
        isContingentTherapy: details.is_contingent_therapy,
        firstName: details.member.first_name,
        lastName: details.member.last_name,
        activeReferral,
        referralList,
        authList: details.auths.map(mapTreatmentAuthSummary),
        memberDetails: {
            memberId: details.member.member_id,
            firstName: details.member.first_name,
            lastName: details.member.last_name,
            dateOfBirth: details.member.dob,
        },
    };
};
